/**
 * THE ONE WAY TO LAZY-LOAD A PAGE (backlog c-028).
 *
 * A tab left open across a deploy still runs the old index.html, which names
 * the old hashed chunks. The deploy removed them, so the tab's next lazy route
 * asks for a file that is gone, the dynamic import rejects, and React.lazy
 * rethrows that during render. With nothing catching it the whole app went
 * blank.
 *
 * `lazyWithReload` is React.lazy with one addition: when the import fails
 * because the chunk could not be fetched (`isChunkLoadError`), it reloads the
 * document ONCE, which fetches the new index.html and with it the new hashes.
 * While the reload is under way the promise never settles, so Suspense keeps
 * the loader up rather than flashing an error.
 *
 * "Once" is a sessionStorage flag keyed by the chunk's URL (by the page's path
 * when the browser names none, as Safari does). A chunk that is really missing
 * (a broken deploy, a CDN outage) would otherwise reload forever; with the
 * flag set, the error goes on to `ChunkErrorBoundary` (app/ChunkErrorBoundary),
 * which tells the user why and offers the reload by hand. No auto-reload when:
 * the browser says it is offline (the reload would fail too, and the boundary
 * says to check the connection), or storage is unavailable (a loop could not
 * be ruled out). Any other error is rethrown unchanged.
 *
 * Every lazy page in app/src goes through here, pinned by
 * utils/__tests__/lazy-chunk-reload.test.tsx.
 */
import { lazy } from 'react';

const FLAG_PREFIX = 'chunk-reload:';

/**
 * Messages a failed module fetch carries: Chromium, Safari, Firefox, and
 * Vite's preload helper (a CSS file of the chunk that is gone). Matched on the
 * message because the browsers throw a plain TypeError.
 */
const CHUNK_LOAD_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || CHUNK_LOAD_MESSAGE.test(error.message);
}

/** The flag for this failure: the chunk's URL if the message names one, else the page. */
function reloadFlag(error: Error): string {
  const url = /(https?:\/\/[^\s'"]+|\/[^\s'"]+\.(?:m?js|css))/.exec(error.message)?.[1];
  return FLAG_PREFIX + (url ?? window.location.pathname);
}

/** Reload the document unless this failure already had its one reload. True when it reloaded. */
function reloadOnce(error: Error): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  const flag = reloadFlag(error);
  try {
    if (sessionStorage.getItem(flag) !== null) return false;
    sessionStorage.setItem(flag, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export const lazyWithReload: typeof lazy = (load) =>
  lazy(() =>
    load().catch((error: unknown) => {
      if (error instanceof Error && isChunkLoadError(error) && reloadOnce(error)) {
        return new Promise<never>(() => {});
      }
      throw error;
    }),
  );
